import { useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';

/**
 * Custom hook for building authenticated headers
 * Centralizes the Bearer token handling for all API calls
 */
export const useAuthHeaders = () => {
    const { token } = useContext(AuthContext);

    /**
     * Builds headers with optional Bearer token and custom headers
     * @param additionalHeaders - Optional additional headers to include
     * @returns Record with headers including Authorization if token exists
     */
    const getAuthHeaders = useCallback(
        (additionalHeaders?: Record<string, string>): Record<string, string> => {
            return {
                ...(token && { Authorization: `Bearer ${token}` }),
                ...additionalHeaders,
            };
        },
        [token]
    );

    /**
     * Builds headers with Content-Type: application/json and optional Bearer token
     * @param additionalHeaders - Optional additional headers to include
     * @returns Record with headers including Content-Type and Authorization if token exists
     */
    const getJsonHeaders = useCallback(
        (additionalHeaders?: Record<string, string>): Record<string, string> => {
            return {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
                ...additionalHeaders,
            };
        },
        [token]
    );

    return {
        token,
        getAuthHeaders,
        getJsonHeaders,
    };
};
